import React, { useMemo } from "react";
import { injectIntl, intlShape } from "@ai360/core/intl";

import { Checkbox, DateInput, Section, SelectInput, SubSection } from "~/core";
import CroppingSeason from "~/reports/components/inputs/cropping-season";
import { ACTIVE_YN } from "~/core/picklist";
import { messages } from "~/reports/i18n-messages";
import { messages as globalMessages } from "~/i18n-messages";
import { model } from "~/reports/data";
import { getParsedCropData, getReportDataByReportType } from "~/reports/data/selectors";
import { useCropList } from "~/admin/setup/customer/use-crop-list";
import {
    IS_SELECTABLE,
    useLoggedInUserProductScopeOrgLevelGuid,
} from "~/admin/setup/customer/use-product-list";

import { FieldAPI } from "@ai360/core";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { getFieldsPerPageOptions } from "../utils";
import { setReportDataValue } from "../data/actions";

type ReportBookReportOptionsProps = {
    intl: intlShape;
};

function ReportBookReportOptions({ intl: { formatMessage } }: ReportBookReportOptionsProps) {
    const dispatch = useAppDispatch();

    const onInputChange = (key: string, value: any) =>
        dispatch(
            setReportDataValue({ key, value, reportType: ReportBookReportOptions.reportName })
        );
    const reportData = useAppSelector(
        getReportDataByReportType(ReportBookReportOptions.reportName)
    );
    const cropScopeOrgLevelGuid = useLoggedInUserProductScopeOrgLevelGuid();
    const { crops: rawCrops } = useCropList({ orgLevelGuid: cropScopeOrgLevelGuid });
    const crops = useMemo(
        () =>
            getParsedCropData(
                rawCrops.map((crop) => ({
                    cropGuid: crop.cropGuid,
                    cropId: String(crop.cropId),
                    cropName: crop.name,
                    activeYn: crop.activeYn,
                    isAgvanceLinked: crop.isAgvanceLinked,
                    ...(crop.isAgvanceLinked === false && {
                        getToolTip: () => formatMessage(globalMessages.cropNotLinkedInAgvance),
                    }),
                }))
            ),
        [rawCrops]
    );

    const croppingSeasonDisabled = !!(
        reportData[model.PROPS_START_DATE] || reportData[model.PROPS_END_DATE]
    );

    return (
        <Section className="report-options" testId="report-book-options-section">
            <SubSection testId="report-book-season-and-crop-subsection">
                <CroppingSeason
                    disabled={croppingSeasonDisabled}
                    required={!croppingSeasonDisabled}
                    reportType={ReportBookReportOptions.reportName}
                />
                <SelectInput<FieldAPI.ICrop>
                    disabled={false}
                    optionIsHiddenKey={IS_SELECTABLE}
                    placeholderText={formatMessage(messages.crop)}
                    options={crops}
                    onChange={(value) => {
                        onInputChange(model.PROPS_CROP_GUID, value ? value.cropGuid : null);
                    }}
                    value={reportData[model.PROPS_CROP_GUID]}
                    testId="report-book-crop-select"
                />
            </SubSection>
            <SubSection testId="report-book-dates-subsection">
                <DateInput
                    disabled={false}
                    placeholderText={formatMessage(messages.startDate)}
                    onChange={(value) =>
                        onInputChange(model.PROPS_START_DATE, value ? value : null)
                    }
                    timeFormat={false}
                    value={reportData[model.PROPS_START_DATE]}
                    testId="report-book-start-date-date"
                />
                <DateInput
                    disabled={false}
                    placeholderText={formatMessage(messages.endDate)}
                    onChange={(value) => onInputChange(model.PROPS_END_DATE, value ? value : null)}
                    timeFormat={false}
                    value={reportData[model.PROPS_END_DATE]}
                    testId="report-book-end-date-date"
                />
            </SubSection>
            <SubSection testId="report-book-group-by-subsection">
                <SelectInput
                    required
                    clearable={false}
                    optionIsHiddenKey={ACTIVE_YN}
                    containerClassNames={["display-options-list"]}
                    placeholderText={formatMessage(messages.reportBookGroupByOption)}
                    options={getFieldsPerPageOptions()}
                    onChange={(value) => {
                        if (value != "field") {
                            onInputChange(model.PROPS_INDIVIDUAL_RB, false);
                        }
                        onInputChange(model.PROPS_REPORT_BOOK_GROUP_BY, value);
                    }}
                    value={reportData[model.PROPS_REPORT_BOOK_GROUP_BY]}
                    testId="report-book-group-by-select"
                />
            </SubSection>
            <SubSection testId="report-book-individual-report-book-subsection">
                <Checkbox
                    className={""}
                    disabled={reportData[model.PROPS_REPORT_BOOK_GROUP_BY] != "field"}
                    label={formatMessage(messages.individualReportBook, {})}
                    onChange={(e, value) => onInputChange(model.PROPS_INDIVIDUAL_RB, value)}
                    value={reportData[model.PROPS_INDIVIDUAL_RB]}
                    testId="report-book-individual-report-book-checkbox"
                />
            </SubSection>
        </Section>
    );
}

ReportBookReportOptions.propTypes = {
    intl: intlShape,
};

ReportBookReportOptions.defaultReportOptions = {
    [model.PROPS_INDIVIDUAL_RB]: false,
    [model.PROPS_REPORT_BOOK_GROUP_BY]: "grower",
};

ReportBookReportOptions.reportPreferences = [];

ReportBookReportOptions.reportName = "ReportBook";

export default injectIntl(ReportBookReportOptions);
